import { useState, useEffect, useRef } from 'react';
import { Sparkles } from 'lucide-react';
import { setAiConsent } from '../utils/aiConsent';
import { markAiNudgeShown, silenceAiNudges } from '../utils/aiNudge';

/**
 * The single AI opt-in prompt used by every web surface (voice, echo, journal,
 * home). It enables consent in place — the user is never sent to Settings to
 * finish a thought they started somewhere else.
 *
 * Deliberately not a blocker: it only ever appears after the user's actual work
 * has already been saved, and "Not now" is a real answer.
 *
 * Props:
 *   open     — render control
 *   userId   — required to post consent
 *   reason   — one line, specific to where this was raised
 *   onEnabled— called after consent is granted (refresh whatever went stale)
 *   onClose  — called on dismiss (either "Not now" or "Don't ask again")
 */
export function AiEnableDialog({ open, userId, reason, onEnabled, onClose }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const confirmRef = useRef(null);

  // Recorded as soon as it appears: an interrupted dialog still counts as
  // "asked", so a reload can't turn one prompt into a loop.
  useEffect(() => {
    if (open) markAiNudgeShown();
  }, [open]);

  // Move focus to the primary action so the dialog is immediately keyboard-
  // operable, and let Escape dismiss it like any other modal.
  useEffect(() => {
    if (!open) return undefined;
    confirmRef.current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving, onClose]);

  if (!open) return null;

  const enable = async () => {
    setSaving(true);
    setError('');
    try {
      // setAiConsent clears the nudge flags for us — every consent change does,
      // wherever it is made from.
      await setAiConsent(userId, { accepted: true });
      onEnabled?.();
    } catch {
      setError('Something went wrong. Please try again.');
      setSaving(false);
    }
  };

  const dismiss = (silence) => {
    if (silence) silenceAiNudges();
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ai-enable-title"
    >
      <div
        className="absolute inset-0 bg-stone-900/50 backdrop-blur-sm"
        onClick={() => !saving && dismiss(false)}
      />
      <div className="relative bg-white dark:bg-[#2D2A27] rounded-[2rem] shadow-2xl w-full max-w-sm p-7 space-y-5 animate-in fade-in slide-in-from-bottom-4 duration-300">
        <div className="flex items-start gap-4">
          <div className="shrink-0 p-3 bg-stone-50 dark:bg-[#1A1816] rounded-2xl border border-stone-100 dark:border-stone-700">
            <Sparkles size={20} className="text-emerald-600" strokeWidth={1.5} />
          </div>
          <h2
            id="ai-enable-title"
            className="font-bold text-stone-800 dark:text-[#F0EDE8] text-lg leading-snug pt-1"
          >
            Turn on AI personalization?
          </h2>
        </div>

        <p className="text-sm text-stone-600 dark:text-stone-300 leading-relaxed">
          {reason}
        </p>
        <p className="text-xs text-stone-400 leading-relaxed">
          Your entries are shared with an AI model so it can power chat,
          transcription and tagging. You can pause it again anytime.
        </p>

        {error && (
          <p className="text-xs text-red-600 bg-red-50 rounded-xl p-3">{error}</p>
        )}

        <div className="flex flex-col gap-2 pt-1">
          <button
            ref={confirmRef}
            onClick={enable}
            disabled={saving}
            className="w-full py-3.5 bg-stone-800 text-white rounded-xl font-semibold hover:bg-black transition-all duration-300 ease-out disabled:opacity-50"
          >
            {saving ? 'Turning on…' : 'Turn on'}
          </button>
          <button
            onClick={() => dismiss(false)}
            disabled={saving}
            className="w-full py-3 text-stone-600 dark:text-stone-300 rounded-xl font-semibold hover:bg-stone-50 dark:hover:bg-[#1A1816] transition-all duration-300 ease-out disabled:opacity-40"
          >
            Not now
          </button>
          <button
            onClick={() => dismiss(true)}
            disabled={saving}
            className="w-full py-2 text-xs text-stone-400 font-medium hover:text-stone-600 transition-all duration-300 ease-out disabled:opacity-40"
          >
            Don&apos;t ask again
          </button>
        </div>
      </div>
    </div>
  );
}
